import React from 'react';
import { StandardTableActions, Badge, statusVariantFromBoolean } from '../../components/ui';
import { Column } from '../../components/DataTable/DataTable';
import { FormSection } from '../../components/Form';
import { Client } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import ClientContactsSection from '../client-contacts/components/ClientContactsSection';
import ActivityLogSection from '../activity-logs/components/ActivityLogSection';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import { DocumentTextIcon } from '../../components/ui/Icons';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Clients',
    description: 'Manage your client database',
    permissionModuleKey: 'clients',
    route: {
      list: '/clients',
      new: '/clients/new',
      detail: (id: number) => `/clients/${id}`,
      edit: (id: number) => `/clients/${id}/edit`,
      summary: (id: number) => `/clients/${id}/summary`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients',
      detail: (id: number) => `/crm-clients/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search clients by name, email, or phone...',
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientName', 'clientName2', 'clientCode', 'contactPerson', 'clientIsActive', 'isClient'
      ],
    },
    {
      name: 'contact',
      label: 'Contact',
      icon: '☎️',
      fields: [
        'clientEmail', 'clientEmail2', 'clientPhone', 'clientPhone2', 'clientWebSite'
      ],
    },
    {
      name: 'location',
      label: 'Location',
      icon: '📍',
      fields: [
        'clientAddress', 'clientCity', 'clientZipCode', 'clientState'
      ],
    },
    {
      name: 'business',
      label: 'Business',
      icon: '💼',
      fields: [
        'clientVATNumber', 'gemiNumber', 'clientPaymentTerms', 'dateStarted'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    // Filter field definitions
    fields: [
      {
        key: 'clientName',
        label: 'Client Name',
        placeholder: 'All Client Name',
        getValue: (item: Client) => item.clientName,
        options: [],
      },
      {
        key: 'clientName2',
        label: 'Client Name 2',
        placeholder: 'All Client Name 2',
        getValue: (item: Client) => item.clientName2,
        options: [],
      },
      {
        key: 'contactPerson',
        label: 'Contact Person',
        placeholder: 'All Contact Person',
        getValue: (item: Client) => item.contactPerson,
        options: [],
      },
      {
        key: 'clientEmail',
        label: 'Email',
        placeholder: 'All Email',
        getValue: (item: Client) => item.clientEmail,
        options: [],
      },
      {
        key: 'clientEmail2',
        label: 'Email 2',
        placeholder: 'All Email 2',
        getValue: (item: Client) => item.clientEmail2,
        options: [],
      },
      {
        key: 'clientPhone',
        label: 'Phone',
        placeholder: 'All Phone',
        getValue: (item: Client) => item.clientPhone,
        options: [],
      },
      {
        key: 'clientPhone2',
        label: 'Phone 2',
        placeholder: 'All Phone 2',
        getValue: (item: Client) => item.clientPhone2,
        options: [],
      },
      {
        key: 'clientAddress',
        label: 'Address',
        placeholder: 'Type to filter address...',
        type: 'text' as const,
        getValue: (item: Client) => item.clientAddress,
        options: [],
      },
      {
        key: 'clientCity',
        label: 'City',
        placeholder: 'All City',
        getValue: (item: Client) => item.clientCity,
        options: [],
      },
      {
        key: 'clientZipCode',
        label: 'Zip Code',
        placeholder: 'All Zip Code',
        getValue: (item: Client) => item.clientZipCode,
        options: [],
      },
      {
        key: 'clientState',
        label: 'State',
        placeholder: 'All State',
        getValue: (item: Client) => item.clientState,
        options: [],
      },
      {
        key: 'clientWebSite',
        label: 'Website',
        placeholder: 'All Website',
        getValue: (item: Client) => item.clientWebSite,
        options: [],
      },
      {
        key: 'clientVATNumber',
        label: 'VAT Number',
        placeholder: 'All VAT Number',
        getValue: (item: Client) => item.clientVATNumber,
        options: [],
      },
      {
        key: 'gemiNumber',
        label: 'GEMI Number',
        placeholder: 'All GEMI Number',
        getValue: (item: Client) => item.gemiNumber,
        options: [],
      },
      {
        key: 'clientCode',
        label: 'Code',
        placeholder: 'All Code',
        getValue: (item: Client) => item.clientCode,
        options: [],
      },
      {
        key: 'clientPaymentTerms',
        label: 'Payment Terms',
        placeholder: 'All Payment Terms',
        getValue: (item: Client) => item.clientPaymentTerms?.toString(),
        options: [],
      },
      {
        key: 'dateStarted',
        label: 'Date Started',
        placeholder: 'All Date Started',
        type: 'date' as const,
        getValue: (item: Client) =>
          item.dateStarted
            ? formatDateGlobal(item.dateStarted)
            : undefined,
        options: [],
      },
      {
        key: 'clientUserName',
        label: 'Username',
        placeholder: 'All Username',
        getValue: (item: Client) => item.clientUserName,
        options: [],
      },
      {
        key: 'clientIsActive',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: Client) => item.clientIsActive ? 'Active' : 'Inactive',
        options: ['Active', 'Inactive'],
      },
      {
        key: 'isClient',
        label: 'Is Client',
        placeholder: 'All',
        type: 'boolean' as const,
        getValue: (item: Client) => item.isClient ? 'Yes' : 'No',
        options: ['Yes', 'No'],
      },
      {
        key: 'clientCreatedAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Client) =>
          item.clientCreatedAt
            ? formatDateGlobal(item.clientCreatedAt)
            : undefined,
        options: [],
      },
      {
        key: 'clientUpdatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: Client) =>
          item.clientUpdatedAt
            ? formatDateGlobal(item.clientUpdatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Client[],
      filters: {
        clientName: string[];
        clientName2: string[];
        contactPerson: string[];
        clientEmail: string[];
        clientEmail2: string[];
        clientPhone: string[];
        clientPhone2: string[];
        clientAddress: string[] | string;
        clientCity: string[];
        clientZipCode: string[];
        clientState: string[];
        clientWebSite: string[];
        clientVATNumber: string[];
        gemiNumber: string[];
        clientCode: string[];
        clientPaymentTerms: string[];
        dateStarted: string[];
        clientUserName: string[];
        clientIsActive: string[];
        isClient: string[];
        clientCreatedAt: string[];
        clientUpdatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client Name filter
        if (filters.clientName.length > 0) {
          const value = (item.clientName || '').toLowerCase();
          const matches = filters.clientName.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client Name 2 filter
        if (filters.clientName2.length > 0) {
          const value = (item.clientName2 || '').toLowerCase();
          const matches = filters.clientName2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Contact Person filter
        if (filters.contactPerson.length > 0) {
          const value = (item.contactPerson || '').toLowerCase();
          const matches = filters.contactPerson.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Email filter
        if (filters.clientEmail.length > 0) {
          const value = (item.clientEmail || '').toLowerCase();
          const matches = filters.clientEmail.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Email 2 filter
        if (filters.clientEmail2.length > 0) {
          const value = (item.clientEmail2 || '').toLowerCase();
          const matches = filters.clientEmail2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Phone filter
        if (filters.clientPhone.length > 0) {
          const value = (item.clientPhone || '').toLowerCase();
          const matches = filters.clientPhone.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Phone 2 filter
        if (filters.clientPhone2.length > 0) {
          const value = (item.clientPhone2 || '').toLowerCase();
          const matches = filters.clientPhone2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Address filter (text filter)
        const addressFilter = filters.clientAddress;
        if (typeof addressFilter === 'string' && addressFilter.trim() !== '') {
          const value = (item.clientAddress || '').toLowerCase();
          if (!value.includes(addressFilter.toLowerCase())) return false;
        } else if (Array.isArray(addressFilter) && addressFilter.length > 0) {
          const value = (item.clientAddress || '').toLowerCase();
          const matches = addressFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // City filter
        if (filters.clientCity.length > 0) {
          const value = (item.clientCity || '').toLowerCase();
          const matches = filters.clientCity.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Zip Code filter
        if (filters.clientZipCode.length > 0) {
          const value = (item.clientZipCode || '').toLowerCase();
          const matches = filters.clientZipCode.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // State filter
        if (filters.clientState.length > 0) {
          const value = (item.clientState || '').toLowerCase();
          const matches = filters.clientState.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Website filter
        if (filters.clientWebSite.length > 0) {
          const value = (item.clientWebSite || '').toLowerCase();
          const matches = filters.clientWebSite.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // VAT Number filter
        if (filters.clientVATNumber.length > 0) {
          const value = (item.clientVATNumber || '').toLowerCase();
          const matches = filters.clientVATNumber.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // GEMI Number filter
        if (filters.gemiNumber.length > 0) {
          const value = (item.gemiNumber || '').toLowerCase();
          const matches = filters.gemiNumber.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Code filter
        if (filters.clientCode.length > 0) {
          const value = (item.clientCode || '').toLowerCase();
          const matches = filters.clientCode.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Payment Terms filter
        if (filters.clientPaymentTerms.length > 0) {
          const value = item.clientPaymentTerms?.toString() || '';
          const matches = filters.clientPaymentTerms.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Date Started filter
        if (filters.dateStarted.length > 0) {
          if (ranges.dateStarted) {
            const range = ranges.dateStarted;
            if (range.start && range.end && item.dateStarted) {
              const itemDate = new Date(item.dateStarted);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.dateStarted
              ? formatDateGlobal(item.dateStarted)
              : '';
            const matches = filters.dateStarted.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Username filter
        if (filters.clientUserName.length > 0) {
          const value = (item.clientUserName || '').toLowerCase();
          const matches = filters.clientUserName.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.clientIsActive.length > 0) {
          const status = item.clientIsActive ? 'Active' : 'Inactive';
          if (!filters.clientIsActive.includes(status)) return false;
        }

        // Is Client filter
        if (filters.isClient.length > 0) {
          const value = item.isClient ? 'Yes' : 'No';
          if (!filters.isClient.includes(value)) return false;
        }

        // Created At filter
        if (filters.clientCreatedAt.length > 0) {
          if (ranges.clientCreatedAt) {
            const range = ranges.clientCreatedAt;
            if (range.start && range.end && item.clientCreatedAt) {
              const itemDate = new Date(item.clientCreatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientCreatedAt
              ? formatDateGlobal(item.clientCreatedAt)
              : '';
            const matches = filters.clientCreatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Updated At filter
        if (filters.clientUpdatedAt.length > 0) {
          if (ranges.clientUpdatedAt) {
            const range = ranges.clientUpdatedAt;
            if (range.start && range.end && item.clientUpdatedAt) {
              const itemDate = new Date(item.clientUpdatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientUpdatedAt
              ? formatDateGlobal(item.clientUpdatedAt)
              : '';
            const matches = filters.clientUpdatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Client',
        showTitle: false,
        variant: 'primary' as const,
        href: '/clients/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Client) => {
      window.location.href = `/clients/${item.clientId}`; // Using direct route, can be changed to use module.route.detail if needed
    },
    keyExtractor: (item: Client) => item.clientId,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new client.',
    noSearchResults: 'No clients found matching your search criteria.',
    noFilterResults: 'No clients match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Client',
    message: 'Are you sure you want to delete this client? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete client. Please try again.',
    handler: async (id: number) => {
      const { deleteClient } = await import('../../services/mutations');
      await deleteClient(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Client Details',
    notFoundTitle: 'Client not found',
    notFoundMessage: 'The client you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Clients',
    defaultTitle: 'Unnamed Client',
    getTitle: (item: Client) => item.clientName || 'Unnamed Client',
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Client',
      subtitle: 'Add a new client to your database',
      submitText: 'Create Client',
    },
    edit: {
      title: 'Edit Client',
      subtitle: 'Update client information',
      submitText: 'Update Client',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading client...',
    errorMessage: 'Failed to save client. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<Client>, files?: File[], filesToDetach?: number[]) => {
      const { createClient } = await import('../../services/mutations');
      const sanitizedData: Partial<Client> = { ...data };
      sanitizedData.clientUserName = sanitizedData.clientUserName?.trim() ? sanitizedData.clientUserName.trim() : null;
      sanitizedData.clientUserPassword = sanitizedData.clientUserPassword?.trim() ? sanitizedData.clientUserPassword : undefined;
      return await createClient(sanitizedData, files);
    },
    update: async (id: number, data: Partial<Client>, files?: File[], filesToDetach?: number[]) => {
      const { updateClient } = await import('../../services/mutations');
      const sanitizedData: Partial<Client> = { ...data };
      sanitizedData.clientUserName = sanitizedData.clientUserName?.trim() ? sanitizedData.clientUserName.trim() : null;
      sanitizedData.clientUserPassword = sanitizedData.clientUserPassword?.trim() ? sanitizedData.clientUserPassword : undefined;
      return await updateClient(id, sanitizedData, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/crm-clients/client/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientName',
      label: 'Client Name',
      type: 'text',
      required: true,
      placeholder: 'Enter client name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientName2',
      label: 'Client Name 2',
      type: 'text',
      placeholder: 'Enter client name 2',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientCode',
      label: 'Client Code',
      type: 'text',
      placeholder: 'Enter client code',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'contactPerson',
      label: 'Contact Person',
      type: 'text',
      placeholder: 'Enter contact person name',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientEmail',
      label: 'Email',
      type: 'email',
      placeholder: 'Enter email address',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
    {
      name: 'clientEmail2',
      label: 'Email 2',
      type: 'email',
      placeholder: 'Enter secondary email address',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
    {
      name: 'clientPhone',
      label: 'Phone',
      type: 'tel',
      placeholder: 'Enter phone number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'clientPhone2',
      label: 'Phone 2',
      type: 'tel',
      placeholder: 'Enter secondary phone number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'clientWebSite',
      label: 'Website',
      type: 'url',
      placeholder: 'Enter website URL',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientAddress',
      label: 'Address',
      type: 'text',
      placeholder: 'Enter street address',
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'clientCity',
      label: 'City',
      type: 'text',
      placeholder: 'Enter city',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'clientZipCode',
      label: 'Zip Code',
      type: 'text',
      placeholder: 'Enter zip code',
      validation: {
        maxLength: 20,
      },
    },
    {
      name: 'clientState',
      label: 'State',
      type: 'text',
      placeholder: 'Enter state',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'clientVATNumber',
      label: 'VAT Number',
      type: 'text',
      placeholder: 'Enter VAT number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'gemiNumber',
      label: 'GEMI Number',
      type: 'text',
      placeholder: 'Enter GEMI number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'sourceUrl',
      label: 'Source URL',
      type: 'url',
      placeholder: 'Enter source URL',
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'clientPaymentTerms',
      label: 'Payment Terms',
      type: 'number',
      placeholder: 'Enter payment terms (days)',
      validation: {
        min: 0,
      },
    },
    {
      name: 'clientNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter notes',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'clientUserName',
      inputName: 'clientPortalAccessUser',
      label: 'Portal username',
      type: 'text',
      placeholder: 'Login username for client portal',
      description: 'Unique username for client to sign in at /portal (leave empty to disable portal access)',
      autoComplete: 'off',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientUserPassword',
      inputName: 'clientPortalAccessSecret',
      label: 'Portal password',
      type: 'password',
      placeholder: 'Set portal password (leave blank to keep current)',
      description: 'Initial password for client portal. Client can change it after first login.',
      autoComplete: 'new-password',
      validation: {
        minLength: 6,
        maxLength: 255,
      },
    },
    {
      name: 'clientIsActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Client is active',
      description: 'Mark whether the client is currently active (must be ON for portal login)',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'isClient',
      label: 'Is Client',
      type: 'switch',
      switchLabel: 'This is a client',
      description: 'Mark whether this is a client',
      labelPosition: 'left',
      defaultValue: true,
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📄',
        gridCols: 'md:grid-cols-12',
        fields: ['clientName', 'clientName2', 'clientCode', 'contactPerson'],
        fieldSpans: {
          clientName: 'md:col-span-6',
          clientName2: 'md:col-span-6',
          clientCode: 'md:col-span-4',
          contactPerson: 'md:col-span-8',
        },
        collapsible: true,
      },
      {
        id: 'contact',
        title: 'Contact Information',
        icon: '☎️',
        gridCols: 'md:grid-cols-12',
        fields: ['clientEmail', 'clientEmail2', 'clientPhone', 'clientPhone2', 'clientWebSite'],
        fieldSpans: {
          clientEmail: 'md:col-span-6',
          clientEmail2: 'md:col-span-6',
          clientPhone: 'md:col-span-6',
          clientPhone2: 'md:col-span-6',
          clientWebSite: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'address',
        title: 'Address Information',
        icon: '📍',
        gridCols: 'md:grid-cols-12',
        fields: ['clientAddress', 'clientCity', 'clientZipCode', 'clientState'],
        fieldSpans: {
          clientAddress: 'md:col-span-12',
          clientCity: 'md:col-span-6',
          clientZipCode: 'md:col-span-3',
          clientState: 'md:col-span-3',
        },
        collapsible: true,
      },
      {
        id: 'additional',
        title: 'Additional Information',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientVATNumber', 'gemiNumber', 'sourceUrl', 'clientPaymentTerms', 'clientNotes'],
        fieldSpans: {
          clientVATNumber: 'md:col-span-6',
          gemiNumber: 'md:col-span-6',
          sourceUrl: 'md:col-span-12',
          clientPaymentTerms: 'md:col-span-6',
          clientNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'portal',
        title: 'Client portal access',
        icon: '🔐',
        gridCols: 'md:grid-cols-2',
        fields: ['clientUserName', 'clientUserPassword'],
        collapsible: true,
      },
      {
        id: 'status',
        title: 'Status',
        icon: '✅',
        gridCols: 'md:grid-cols-2',
        fields: ['clientIsActive', 'isClient'],
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns (needs to be in a separate function to use JSX)
export const getColumns = (formatDate: (dateString?: string) => string): Column<Client>[] => [
  {
    key: 'clientName',
    header: 'Client',
    sortable: true,
    render: (value, row) => {
      const attachmentCount = row.FileAttachment?.length ?? row._count?.FileAttachment ?? 0;
      const clientName = row.clientName || 'Unnamed Client';
      const truncatedName = clientName.length > 50 ? clientName.substring(0, 50) + '...' : clientName;
      const count = row._count?.clientCalls || 0;
      const lastCallDate = row.clientCalls?.[0]?.clientCallDate;
      const hasSecondLine = !!row.contactPerson || count > 0 || !!lastCallDate;

      return (
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate">
              {row.clientVATNumber ? `${truncatedName} (${row.clientVATNumber})` : truncatedName}
            </span>
            {attachmentCount > 0 && (
              <DocumentTextIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            )}
          </div>
          {hasSecondLine && (
            <div className="mt-0.5 flex items-center gap-1.5 min-w-0 text-xs text-gray-500 dark:text-gray-400">
              {row.contactPerson && <span className="truncate">{row.contactPerson}</span>}
              {row.contactPerson && count > 0 && <span>-</span>}
              {count > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-300">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.129a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.209-.503l4.493 1.498A1 1 0 0121 15.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  {count}
                </span>
              )}
              {count > 0 && lastCallDate && <span>{formatDate(lastCallDate)}</span>}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'clientEmail',
    header: 'Email',
    sortable: true,
    render: (value, row) => (
      <div className="min-w-0">
        <div>{value || '-'}</div>
        {row.clientEmail2 && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {row.clientEmail2}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientPhone',
    header: 'Phone',
    sortable: true,
    render: (value, row) => (
      <div className="min-w-0">
        <div>{value || '-'}</div>
        {row.clientPhone2 && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {row.clientPhone2}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientAddress',
    header: 'Address',
    sortable: true,
    render: (value, row) => {
      const addressLine = value
        ? (String(value).length > 50 ? String(value).substring(0, 50) + '...' : String(value))
        : '-';
      const locationParts = [
        row.clientCity,
        row.clientZipCode,
        row.clientState,
        row.clientCountryDescr?.pc_descr || (row.clientCountryId ? String(row.clientCountryId) : ''),
      ].filter(Boolean);

      return (
        <div className="min-w-0">
          <div>{addressLine}</div>
          {locationParts.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {locationParts.join(', ')}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'clientWebSite',
    header: 'Web',
    sortable: true,
    render: (value) => (
      value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          title={String(value)}
          aria-label={`Open website ${String(value)}`}
          className="inline-flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 115.656 5.656l-1.5 1.5"
            />
          </svg>
        </a>
      ) : (
        '-'
      )
    ),
  },
  {
    key: 'clientVATNumber',
    header: 'Identification',
    sortable: true,
    render: (_value, row) => (
      <div className="min-w-0">
        <div>{row.gemiNumber || '-'}</div>
        {row.clientCode && (
          <div className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 4h14a2 2 0 012 2v7H3V6a2 2 0 012-2z"
              />
            </svg>
            <span>{row.clientCode}</span>
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientIsActive',
    header: 'Status',
    sortable: true,
    render: (value, row) => (
      <div className="flex items-center space-x-2 flex-nowrap">
        <Badge variant={statusVariantFromBoolean(!!row.clientIsActive)}>
          {row.clientIsActive ? 'Active' : 'Inactive'}
        </Badge>
        {row.isClient && (
          <Badge variant="info">Client</Badge>
        )}
      </div>
    ),
  },
  {
    key: 'clientPaymentTerms',
    header: 'Payment Terms',
    sortable: true,
    render: (value) => value ? `${value} days` : '-',
  },
  {
    key: 'clientTaxAuthority',
    header: 'Tax',
    sortable: true,
    render: (value) => value || '-',
  },
  {
    key: 'dateStarted',
    header: 'Date Started',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'sourceUrl',
    header: 'Source URL',
    sortable: true,
    render: (value) => (
      value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      ) : (
        '-'
      )
    ),
  },
  {
    key: 'clientCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'clientUpdatedAt',
    header: 'Updated At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Client,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      fields: [
        {
          label: 'Client Name',
          value: item.clientName || '-',
        },
        {
          label: 'Client Name 2',
          value: item.clientName2 || '-',
          condition: !!item.clientName2,
        },
        {
          label: 'Client Code',
          value: item.clientCode || '-',
          condition: !!item.clientCode,
        },
        {
          label: 'Contact Person',
          value: item.contactPerson || '-',
          condition: !!item.contactPerson,
        },
        {
          label: 'VAT Number',
          value: item.clientVATNumber || '-',
          condition: !!item.clientVATNumber,
        },
        {
          label: 'GEMI Number',
          value: item.gemiNumber || '-',
          condition: !!item.gemiNumber,
        },
      ],
    },
    {
      title: 'Contact Information',
      fields: [
        {
          label: 'Email',
          value: item.clientEmail ? (
            <a href={`mailto:${item.clientEmail}`} className="text-indigo-600 hover:text-indigo-700">
              {item.clientEmail}
            </a>
          ) : (
            '-'
          ),
          condition: !!item.clientEmail,
        },
        {
          label: 'Email 2',
          value: item.clientEmail2 ? (
            <a href={`mailto:${item.clientEmail2}`} className="text-indigo-600 hover:text-indigo-700">
              {item.clientEmail2}
            </a>
          ) : (
            '-'
          ),
          condition: !!item.clientEmail2,
        },
        {
          label: 'Phone',
          value: item.clientPhone ? (
            <a href={`tel:${item.clientPhone}`} className="text-indigo-600 hover:text-indigo-700">
              {item.clientPhone}
            </a>
          ) : (
            '-'
          ),
          condition: !!item.clientPhone,
        },
        {
          label: 'Phone 2',
          value: item.clientPhone2 ? (
            <a href={`tel:${item.clientPhone2}`} className="text-indigo-600 hover:text-indigo-700">
              {item.clientPhone2}
            </a>
          ) : (
            '-'
          ),
          condition: !!item.clientPhone2,
        },
        {
          label: 'Website',
          value: item.clientWebSite ? (
            <a
              href={item.clientWebSite}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700"
            >
              {item.clientWebSite}
            </a>
          ) : (
            '-'
          ),
          condition: !!item.clientWebSite,
        },
      ],
    },
    {
      title: 'Address',
      fields: [
        {
          label: 'Street Address',
          value: item.clientAddress || '-',
          condition: !!item.clientAddress,
        },
        {
          label: 'City',
          value: item.clientCity || '-',
          condition: !!item.clientCity,
        },
        {
          label: 'Zip Code',
          value: item.clientZipCode || '-',
          condition: !!item.clientZipCode,
        },
        {
          label: 'State',
          value: item.clientState || '-',
          condition: !!item.clientState,
        },
      ],
      condition: !!(item.clientAddress || item.clientCity || item.clientZipCode || item.clientState),
    },
    {
      title: 'Additional Information',
      fields: [
        {
          label: 'Notes',
          value: item.clientNotes ? (
            <span className="whitespace-pre-wrap">{item.clientNotes}</span>
          ) : (
            '-'
          ),
          condition: !!item.clientNotes,
        },
        {
          label: 'Payment Terms',
          value: item.clientPaymentTerms ? `${item.clientPaymentTerms} days` : '-',
          condition: !!item.clientPaymentTerms,
        },
        {
          label: 'Created At',
          value: formatDate(item.clientCreatedAt),
        },
        {
          label: 'Last Updated',
          value: formatDate(item.clientUpdatedAt),
          condition: !!item.clientUpdatedAt,
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/crm-clients/client/${item.clientId}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `client_${item.clientId}_files.zip`,
            getViewFileUrl: (fileId) => `/crm-clients/client/${item.clientId}/files/${fileId}`,
          }),
        },
      ],
    },
    {
      title: 'Contacts',
      icon: '👤',
      fields: [
        {
          label: '',
          value: React.createElement(ClientContactsSection, { clientId: item.clientId }),
        },
      ],
    },
    {
      title: 'Activity Log',
      icon: '📋',
      fields: [
        {
          label: '',
          value: React.createElement(ActivityLogSection, { entityType: 'Client', entityId: item.clientId }),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: Client) => {
  return (
    <>
      <Badge variant={statusVariantFromBoolean(!!item.clientIsActive)} className="px-3 py-1 text-sm">
        {item.clientIsActive ? 'Active' : 'Inactive'}
      </Badge>
      {item.isClient && (
        <Badge variant="info" className="px-3 py-1 text-sm">
          Client
        </Badge>
      )}
    </>
  );
};

// Helper function to get form sections
export const getFormSections = (): FormSection[] => {
  return [
    {
      title: 'Basic Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientName',
          label: 'Client Name',
          type: 'text',
          required: true,
        },
        {
          name: 'clientName2',
          label: 'Client Name 2',
          type: 'text',
        },
        {
          name: 'clientCode',
          label: 'Client Code',
          type: 'text',
        },
        {
          name: 'contactPerson',
          label: 'Contact Person',
          type: 'text',
        },
      ],
    },
    {
      title: 'Contact Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientEmail',
          label: 'Email',
          type: 'email',
        },
        {
          name: 'clientEmail2',
          label: 'Email 2',
          type: 'email',
        },
        {
          name: 'clientPhone',
          label: 'Phone',
          type: 'tel',
        },
        {
          name: 'clientPhone2',
          label: 'Phone 2',
          type: 'tel',
        },
        {
          name: 'clientWebSite',
          label: 'Website',
          type: 'url',
        },
      ],
    },
    {
      title: 'Address Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientAddress',
          label: 'Address',
          type: 'text',
          colSpan: 2,
        },
        {
          name: 'clientCity',
          label: 'City',
          type: 'text',
        },
        {
          name: 'clientZipCode',
          label: 'Zip Code',
          type: 'text',
        },
        {
          name: 'clientState',
          label: 'State',
          type: 'text',
        },
      ],
    },
    {
      title: 'Additional Information',
      gridCols: 2,
      fields: [
        {
          name: 'clientVATNumber',
          label: 'VAT Number',
          type: 'text',
        },
        {
          name: 'gemiNumber',
          label: 'GEMI Number',
          type: 'text',
        },
        {
          name: 'sourceUrl',
          label: 'Source URL',
          type: 'url',
        },
        {
          name: 'clientPaymentTerms',
          label: 'Payment Terms',
          type: 'number',
        },
        {
          name: 'clientNotes',
          label: 'Notes',
          type: 'textarea',
          colSpan: 2,
        },
      ],
    },
    {
      title: 'Status',
      gridCols: 2,
      fields: [
        {
          name: 'clientIsActive',
          label: 'Active',
          type: 'checkbox',
        },
        {
          name: 'isClient',
          label: 'Is Client',
          type: 'checkbox',
        },
      ],
    },
  ];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'settings':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Client,
  onDelete: (id: number) => void,
  _colors?: unknown,
  listContext?: { onOpenSummary?: (item: Client) => void },
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.clientId)}
      editTo={moduleConfig.module.route.edit(item.clientId)}
      onDelete={() => onDelete(item.clientId)}
      onSummary={listContext?.onOpenSummary ? () => listContext.onOpenSummary?.(item) : undefined}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Client> => {
  return {
    clientName: '',
    clientName2: '',
    clientCode: '',
    contactPerson: '',
    clientAddress: '',
    clientCity: '',
    clientZipCode: '',
    clientState: '',
    clientWebSite: '',
    clientPhone: '',
    clientPhone2: '',
    clientEmail: '',
    clientEmail2: '',
    clientNotes: '',
    clientVATNumber: '',
    clientPaymentTerms: undefined,
    clientTaxAuthority: undefined,
    clientIsActive: true,
    isClient: true,
    gemiNumber: '',
    sourceUrl: '',
    clientUserName: '',
    clientUserPassword: '',
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Client): Partial<Client> => {
  return {
    clientName: item.clientName || '',
    clientName2: item.clientName2 || '',
    clientCode: item.clientCode || '',
    contactPerson: item.contactPerson || '',
    clientAddress: item.clientAddress || '',
    clientCity: item.clientCity || '',
    clientZipCode: item.clientZipCode || '',
    clientState: item.clientState || '',
    clientWebSite: item.clientWebSite || '',
    clientPhone: item.clientPhone || '',
    clientPhone2: item.clientPhone2 || '',
    clientEmail: item.clientEmail || '',
    clientEmail2: item.clientEmail2 || '',
    clientNotes: item.clientNotes || '',
    clientVATNumber: item.clientVATNumber || '',
    clientPaymentTerms: item.clientPaymentTerms,
    clientTaxAuthority: item.clientTaxAuthority,
    clientIsActive: item.clientIsActive,
    isClient: item.isClient,
    gemiNumber: item.gemiNumber || '',
    sourceUrl: item.sourceUrl || '',
    clientUserName: '',
    clientUserPassword: '',
  };
};

export type ModuleConfig = typeof moduleConfig;
